import { getFontStack } from "utils/tenantBranding"

const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{6})$/

/** Defaults aligned with portal tenant branding (tenantTheme). */
const DEFAULT_THEME = {
  primaryColor: "#2A3042",
  secondaryColor: "#74788D",
  iconColor: "#A6B0CF",
  primaryLight: "#FFFFFF",
  primaryDeep: "#1E293B",
  fontFamily: "inter",
  pageBackground: {
    // Same pairing as .login-tenant: button primary → sidebar icon
    colorStart: "#2A3042",
    colorEnd: "#A6B0CF",
    direction: "to bottom",
  },
  buttons: {
    backgroundColor: "#2A3042",
    textColor: "#FFFFFF",
    borderRadius: 12,
  },
  inputs: {
    backgroundColor: "#FFFFFF",
    textColor: "#1E293B",
    borderColor: "#CED4DA",
    borderRadius: 12,
  },
}

const parseHex = (value, fallback) => {
  const trimmed = String(value || "").trim()
  return HEX_COLOR_REGEX.test(trimmed) ? trimmed : fallback
}

const hexToRgb = hex => {
  const n = parseInt(hex.slice(1), 16)
  return {
    r: (n >> 16) & 255,
    g: (n >> 8) & 255,
    b: n & 255,
  }
}

const rgba = (hex, alpha) => {
  const { r, g, b } = hexToRgb(hex)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const isLightColor = hex => {
  const { r, g, b } = hexToRgb(hex)
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62
}

const resolveFontFamily = fontFamily => {
  const value = String(fontFamily || "").trim()
  if (!value) {
    return getFontStack(DEFAULT_THEME.fontFamily)
  }
  const stack = getFontStack(value)
  if (
    stack &&
    ["inter", "poppins", "dm-sans", "system"].includes(value.toLowerCase())
  ) {
    return stack
  }
  return value
}

const px = (value, fallback) => {
  const num = Number(value)
  if (!Number.isFinite(num)) {
    return `${fallback}px`
  }
  return `${Math.min(24, Math.max(4, Math.round(num)))}px`
}

export const buildRatingThemeStyle = (theme = {}) => {
  const secondary = parseHex(theme.secondaryColor, DEFAULT_THEME.secondaryColor)
  const deep = parseHex(theme.primaryDeep, DEFAULT_THEME.primaryDeep)
  const btnBg = parseHex(
    theme.buttons?.backgroundColor,
    DEFAULT_THEME.buttons.backgroundColor
  )
  const btnText = parseHex(
    theme.buttons?.textColor,
    DEFAULT_THEME.buttons.textColor
  )
  const iconColor = parseHex(
    theme.iconColor || theme.pageBackground?.colorEnd,
    DEFAULT_THEME.iconColor
  )
  const inputBg = parseHex(
    theme.inputs?.backgroundColor,
    DEFAULT_THEME.inputs.backgroundColor
  )
  const inputText = parseHex(theme.inputs?.textColor, deep)
  const inputBorder = parseHex(
    theme.inputs?.borderColor,
    DEFAULT_THEME.inputs.borderColor
  )

  // Exact same formula as tenant customized login (.login-tenant):
  // radial-gradient(circle, --tenant-button-primary-bg-color 0%, --tenant-sidebar-icon-color 100%)
  const gradientCenter = parseHex(theme.pageBackground?.colorStart, btnBg)
  const gradientOuter = parseHex(theme.pageBackground?.colorEnd, iconColor)
  const bgImage = `radial-gradient(circle, ${gradientCenter} 0%, ${gradientOuter} 100%)`

  // Hero text sits over the center of the gradient (button primary color).
  const lightBg = isLightColor(gradientCenter)
  const onBg = lightBg ? deep : "#FFFFFF"

  return {
    "--rp-bg": gradientCenter,
    "--rp-bg-soft": gradientOuter,
    "--rp-bg-image": bgImage,
    "--rp-accent": btnBg,
    "--rp-accent-soft": secondary,
    "--rp-accent-ring": rgba(btnBg, 0.18),
    "--rp-ink": inputText,
    "--rp-muted": rgba(inputText, 0.62),
    "--rp-card": "#ffffff",
    "--rp-on-bg": onBg,
    "--rp-on-bg-muted": lightBg
      ? rgba(deep, 0.72)
      : "rgba(255, 255, 255, 0.82)",
    "--rp-btn-text": btnText,
    "--rp-btn-radius": px(theme.buttons?.borderRadius, 12),
    "--rp-input-bg": inputBg,
    "--rp-input-text": inputText,
    "--rp-input-border": inputBorder,
    "--rp-input-radius": px(theme.inputs?.borderRadius, 12),
    "--rp-font": resolveFontFamily(theme.fontFamily),
    "--rp-eyebrow-bg": rgba(onBg, 0.1),
    "--rp-eyebrow-border": rgba(onBg, 0.18),
  }
}
